import { BicepsFlexed, PiggyBank, Users, Users2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { ActivityIndicator } from "react-native";
import { View, XStack, YStack } from "tamagui";
import type { FlowTemplateData } from "../../../../templates/generator/types";
import { PlaniraPlanImage } from "../../../../templates/types";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { convertTemplateToPlanira } from "../../../templates/convertTemplateToPlanira";
import { useCreatePlanFromTemplate } from "../../../templates/useCreatePlanFromTemplate";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import type { BusinessPlanModel } from "../BusinessPlanModel";
import { BusinessPlaniraJoinRow } from "../ui/BusinessPlanirasList";
import { CurrentTeamOrJoinList, TeamRow } from "./BusinessProfileScreen";

const createBusinessPlaniraFlow = (
  businessPlan: BusinessPlanModel,
): FlowTemplateData => {
  return {
    ...businessPlan.data.planInfo,

    type: "NotUsed",
    slug: "NotUSed",
    description: "", // n sot used..
    longDescription: "", // Only used in Gatsby...
    emoji: "🎉",
    titleHelp: "",

    imageType: PlaniraPlanImage.Party,
    suggestedTitles: [],

    modules: [], // no startup modules, covered in business plan

    onTemplateComplete: {
      outroTitle: "You are all set!",
      outroDescription: "Fantastic work! You are all set for this time.",
    },
  };
};

type Props = {
  businessPlanUid: string;
};

export const BusinessCreatePlaniraUI = observer(
  ({ businessPlanUid }: Props) => {
    const { businessPlansStore, plansStore } = useStores();
    const { plansApi } = useApi();
    const navigation = usePlanNavigation();
    const createPlanira = useCreatePlanFromTemplate();

    const [isCreating, setIsCreating] = React.useState(false);
    const [showList, setShowList] = React.useState(false);

    const businessPlan = businessPlansStore.byId.get(businessPlanUid);

    if (!businessPlan) {
      throw Error("No data found");
    }

    const currentTeam = businessPlan.currentTeam;
    const planId = currentTeam?.planUid;

    // Load plan
    React.useEffect(() => {
      if (planId && !plansStore.byId.get(planId)) {
        plansApi.getById(planId);
      }
    }, [planId, plansStore, plansApi]);

    const plan = planId ? plansStore.byId.get(planId) : undefined;

    if (isCreating) {
      return (
        <Box>
          <View height={250} alignItems="center" justifyContent="center">
            <YStack gap="$medium">
              <ActivityIndicator size={"large"} color={"#ffd84e"} />
              <Body muted>Hold on!</Body>
            </YStack>
          </View>
        </Box>
      );
    }

    return (
      <>
        {!showList && (
          <>
            <CurrentTeamOrJoinList businessPlan={businessPlan} hideLeaveCta />

            {currentTeam && (
              <Box>
                <Label>Your own private team plan section</Label>
                <Body>
                  This is optional - but you can create a planning page where
                  you as a team can plan or discuss together
                </Body>

                <Subtext>
                  You will be able to create polls, set reminders and keep track
                  of each other statuses in the main event.
                </Subtext>

                <Label>Examples where a private Planira is of good use</Label>

                <XStack gap="$medium" alignItems="center">
                  <Users />
                  <Body>You need extra alignment within your group</Body>
                </XStack>
                <XStack gap="$medium" alignItems="center">
                  <PiggyBank />
                  <Body>You want a place to keep track and share expenses</Body>
                </XStack>
                <XStack gap="$medium" alignItems="center">
                  <BicepsFlexed />
                  <Body>You can start competitions about anything</Body>
                </XStack>

                {!showList && (
                  <Button onPress={() => setShowList(true)}>Let's go</Button>
                )}
              </Box>
            )}
          </>
        )}

        {showList && (
          <>
            {plan && (
              <Box>
                <Body muted>
                  Your team already have a private plan page created, apply now
                  to join.
                </Body>
                <BusinessPlaniraJoinRow
                  businessPlan={businessPlan}
                  plan={plan}
                />
              </Box>
            )}

            {!plan && (
              <Box>
                <Label>Create new Team Page</Label>

                {!!businessPlan.currentTeam && (
                  <TeamRow
                    team={businessPlan.currentTeam}
                    businessPlan={businessPlan}
                    hideTeamName
                  />
                )}

                <Body>
                  Since your team does not have a page yet, you can create a
                  team page with the current members, more members can apply
                  after creation when needed.
                </Body>

                <Button
                  icon={Users2}
                  onPress={async () => {
                    setIsCreating(true);
                    // Create and navigate to plan without adding user

                    const template = convertTemplateToPlanira(
                      createBusinessPlaniraFlow(businessPlan),
                    );

                    // Set modules to be kept in creation
                    const modulesToCreate =
                      template.sections[1].modules.map((m, i) => ({
                        ...m,
                        keep: true,
                        index: i,
                      })) ?? [];

                    // Navigate to the new one
                    const plan = await createPlanira(
                      template,
                      modulesToCreate,
                      businessPlan.data.planInfo.title,
                      undefined,
                      true,
                      {
                        businessPlanUid,
                        joinOptions: {
                          needToApply: true,
                          waitingForApproval: [],
                        },
                      },
                    );

                    // Add planUid to current user
                    await businessPlan.addNewPlanira(plan);

                    // Add all current members
                    if (businessPlan.currentTeam) {
                      for (const teamMemberUserUid of businessPlan.currentTeam
                        .userUids) {
                        await plan.addUserIdToPlan(teamMemberUserUid);
                      }
                    }

                    // Navigate to correct page
                    navigation.navigate("BusinessTeam", {
                      businessUid: businessPlanUid,
                    });

                    setIsCreating(false);
                  }}
                >
                  Let's go
                </Button>
              </Box>
            )}
          </>
        )}
      </>
    );
  },
);
